import type { CalculationStatus } from "./Calculation";
import type { Operator, Tariff } from "./Operator";
import type { Voivodeship } from "./Voivodeship";

export interface Form {
  id: string;
  customer: FormStep5CustomerResponse | null;
  calculation_status: CalculationStatus;
}

export const PROFILE_METHODS = ["PREDEFINED_PROFILE", "TABLE_INPUT", "FILE_INPUT"] as const;
export const PROFILE_TYPES = ["ONE_SHIFT", "TWO_SHIFTS", "THREE_SHIFTS"] as const;
export const TIMEFRAME_TYPES = ["MONTHLY", "YEARLY"] as const;
export const BILLING_TYPES = ["dynamic", "constant", "tariff"] as const;

export type ProfileCompletionForm = (typeof PROFILE_METHODS)[number];
export type ProfileType = (typeof PROFILE_TYPES)[number];
export type Timeframe = (typeof TIMEFRAME_TYPES)[number];
export type BillingType = (typeof BILLING_TYPES)[number];

export const STEP_KEYS = ["step1", "step2", "step3", "step4", "step5", "summary"] as const;
export type StepKey = (typeof STEP_KEYS)[number];
export type StepTitle = "step1" | "step2" | "step3" | "step4" | "step5" | "step6";
export type StepDataState = "unknown" | "sending" | "sent" | "missing";

export interface FormStep1EnergyConsumption {
  profile_completion_form: ProfileCompletionForm;
  table_input: number[] | null;
  file_input: File | null;
  predefined_profile: ProfileType | null;
  usage_timeframe: Timeframe | null;
  usage_timeframe_value: string | null;
}

export interface FormStep1EnergyConsumptionResponse
  extends Omit<FormStep1EnergyConsumption, "table_input" | "file_input"> {
  id: number;
  file_input: string | null;
  table_input: (string | null)[] | null;
  customer: FormStep5Customer | null;
}

export interface FormStep2OperatorAndTariff {
  operator_id: number;
  tariff_id: number;
}

export interface FormStep2OperatorAndTariffResponse {
  id: number;
  operator: Operator;
  tariff: Tariff;
  customer: FormStep5Customer | null;
}

export interface FormStep3EnergyBilling {
  energy_settlement_type: BillingType;
  margin_rate: string | null;
  fixed_monthly_fee: string | null;
  fixed_price: string | null;
  tariff_id: number | null;
}

export interface FormStep3EnergyBillingResponse {
  id: number;
  energy_settlement_type: BillingType;
  margin_rate: string | null;
  fixed_monthly_fee: string | null;
  fixed_price: string | null;
  tariff: Tariff | null;
  customer: FormStep5Customer | null;
}

export interface FormStep4ConnectionAndInstallation {
  connection_condition_import: string | null;
  connection_condition_export: string | null;
  is_possible_to_sell_excess_energy: boolean;
  has_photovoltaic_installation: boolean;
  installation_capacity: string | null;
  inverter_power: string | null;
  voivodeship: Voivodeship | null;
}

export interface FormStep4ConnectionAndInstallationResponse
  extends FormStep4ConnectionAndInstallation {
  id: number;
  customer: FormStep5Customer | null;
}

export interface FormStep5Customer {
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  company: string;
  privacy_policy_rodo: boolean;
  marketing_email_consent: boolean;
  marketing_phone_consent: boolean;
  newsletter_consent: boolean;
  data_processing_consent: boolean;
}

export interface FormStep5CustomerResponse extends FormStep5Customer {
  id: string;
}
